import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import FilterBar, { type FilterState } from "@/components/base/FilterBar";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { getAllDecades, getAllDirectors, getAllGenres, searchMovies, sortMovies } from "@/utils/movieUtils";
import type { SortKey } from "@/types";

const emptyFilters: FilterState = { keyword: "", genres: [], directors: [], decades: [] };
const hotCategories = ["1990s", "genre-crime", "genre-ancient", "top-rated"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  const [inputValue, setInputValue] = useState(query);
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("popular");

  const results = useMemo(() => {
    let result = searchMovies(movies, query);
    if (filters.keyword.trim() && filters.keyword.trim() !== query.trim()) {
      result = searchMovies(result, filters.keyword);
    }
    if (filters.decades.length > 0) {
      result = result.filter((movie) => filters.decades.includes(movie.decade));
    }
    if (filters.genres.length > 0) {
      result = result.filter((movie) => movie.genre.some((genre) => filters.genres.includes(genre)));
    }
    if (filters.directors.length > 0) {
      result = result.filter((movie) => filters.directors.includes(movie.director));
    }
    return sortMovies(result, sort);
  }, [query, filters, sort]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = inputValue.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
    setFilters(emptyFilters);
  };

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-800">搜索</span>
      </nav>

      <header className="mt-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影片检索</h1>
        <p className="mt-1.5 text-sm text-foreground-600">支持片名、导演、主演、类型与剧情关键词的模糊查询</p>

        <form onSubmit={handleSubmit} className="mt-5 flex w-full max-w-2xl items-center overflow-hidden rounded-full border border-background-300 bg-background-50">
          <span className="flex h-12 w-12 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-lg" />
          </span>
          <input
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            type="search"
            name="search_query"
            placeholder="输入片名 / 导演 / 关键词"
            className="h-12 flex-1 bg-transparent pr-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="m-1 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        {query && (
          <p className="mt-3 text-sm text-foreground-600">
            「<span className="font-medium text-primary-600">{query}</span>」的搜索结果：共
            <span className="mx-1 font-semibold text-foreground-900">{results.length}</span>部影片
          </p>
        )}
      </header>

      <div className="mt-6">
        <FilterBar
          options={{
            genres: getAllGenres(movies),
            directors: getAllDirectors(movies),
            decades: getAllDecades(movies),
          }}
          value={filters}
          onChange={setFilters}
          sort={sort}
          onSortChange={setSort}
          onReset={() => setFilters(emptyFilters)}
        />
      </div>

      {results.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-5 rounded-lg border border-background-300/70 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {query ? "没有找到匹配的影片" : "输入关键词开始检索"}
            </h2>
            <p className="mt-1.5 max-w-md text-sm text-foreground-600">
              {query ? "试试更简短的关键词，或从下面的热门分类中浏览。" : "你可以搜索片名、导演、主演或剧情关键词。"}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {hotCategories.map((key) => {
              const category = categories.find((item) => item.key === key);
              if (!category) return null;
              return (
                <Link
                  key={key}
                  to={`/category/${key}`}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {category.name}
                </Link>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {results.map((movie, index) => (
            <MovieCard key={movie.id} movie={movie} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}